import { CaseInsensitiveMap } from "../DataTypes/CaseInsensitiveMap";
import { GlobalContext } from "../GlobalContext";

export interface INumericEnum {
    [id: number]: string;
}

export interface IStringEnum {
    [id: string]: string;
}

export type IEnum = INumericEnum | IStringEnum;

/**
 * Pomocné metody pro práci s enumy
 * @includeToDoc
 */
export class EnumUtils {
    /**
     * Vrací hodnotu položky předaného enumu podle předané textové hodnoty (hledá v klíčích enumu). Pokud enum nemá odpovídající položku, vrací undefined.
     * @param targetEnum Typ enumu, jehož hodnota se má vracet
     * @param value Textová hodnota pro výběr z předaného enumu
     * @param caseSensitive Pokud není true, tak ignoruje velikost písmen
     */
    public static getEnumValueFromName<T>(targetEnum: T, value: string, caseSensitive?: boolean): T[keyof T] | undefined {
        if (caseSensitive) {
            const enumValueName: keyof typeof targetEnum = value as keyof typeof targetEnum;
            return targetEnum[enumValueName];
        } else {
            const mapped: [string, keyof T][] = Object.keys(targetEnum).map(k => {
                return [k.toLowerCase(), k as keyof typeof targetEnum];
            });
            const map = new CaseInsensitiveMap<keyof typeof targetEnum>(mapped);
            return map.get(value) ? targetEnum[map.get(value)!] : undefined;
        }
    }

    /**
     * Vrací hodnotu položky předaného enumu podle předané textové hodnoty (hledá v hodnotách enumu). Pokud enum nemá odpovídající položku, vrací undefined.
     * @param targetEnum Typ enumu, jehož hodnota se má vracet
     * @param value Textová hodnota pro výběr z předaného enumu
     * @param caseSensitive Pokud není true, tak ignoruje velikost písmen
     */
    public static getEnumValueFromValue<T>(targetEnum: T, value: string, caseSensitive?: boolean): T[keyof T] | undefined {
        if (value == null) {
            return undefined;
        }
        const allValues = Object.values(targetEnum);
        if (caseSensitive) {
            return allValues.find(enumValue => typeof enumValue === "string" && enumValue === value);
        } else {
            value = value?.toLowerCase();
            return allValues.find(enumValue => typeof enumValue === "string" && enumValue.toLowerCase() === value);
        }
    }

    /**
     * Vrací pole textových reprezentací hodnot enumu, který je číselně indexován
     * @param targetEnum Typ enumu, jehož hodnoty se mají vracet
     */
    public static getNumericEnumStringValues<T extends INumericEnum>(targetEnum: T): string[] {
        const resultValues: string[] = [];
        for (const value of Object.keys(targetEnum)) {
            const enumValueName: keyof typeof targetEnum = value as keyof typeof targetEnum;
            if (typeof targetEnum[enumValueName] === "number")
                resultValues.push(value);
        }
        return resultValues;
    }

    /**
     * Vrací pole textových hodnot stringového enumu
     * @param targetEnum Typ enumu, jehož hodnoty se mají vracet
     */
    public static getStringEnumValues<T extends IStringEnum>(targetEnum: T): string[] {
        const resultValues: string[] = [];
        for (const value of Object.keys(targetEnum)) {
            const enumValueName: keyof typeof targetEnum = value as keyof typeof targetEnum;
            if (typeof targetEnum[enumValueName] === "string")
                resultValues.push(value);
        }
        return resultValues;
    }

    /**
     * Prohledá hodnoty enumu a pokud enum obsahuje hledanou hodnotu, vrací true
     * @param targetEnum Enum které se má prohledávat
     * @param value Hledaná textová hodnota
     */
    public static hasEnumValue<T>(targetEnum: T, value: string | number): boolean {
        return Object.values(targetEnum).includes(value);
    }

    /**
     * Vrací pole možných číselných hodnot hodnot enumu, který je číselně indexován
     * @param targetEnum Enum jehož hodnoty se mají vracet
     */
    public static getNumericEnumValues<T>(targetEnum: T): Array<number> {
        const resultValues: number[] = [];
        for (const value of Object.keys(targetEnum)) {
            const enumValueName: keyof typeof targetEnum = value as keyof typeof targetEnum;
            if (typeof targetEnum[enumValueName] === "string") {
                const numericValue = Number(value);
                if (!isNaN(numericValue))
                    resultValues.push(numericValue);
            }
        }
        return resultValues;
    }

    /**
     * Provede konverzi enumu na pole s jeho řetězcovou reprezentací
     * @param sourceEnum Typ enumu, jež má být převeden na pole
     */
    public static getEnumAsArray<T extends INumericEnum>(sourceEnum: T): string[] {
        return Object.values(sourceEnum).filter(value => typeof value === "string") as string[];
    }
    /**
     * Zjistí, zda hodnota masky obsahuje zadaný flag
     * @param enumValue Číselná hodnota masky
     * @param flag Flag, bit určující hodnotu
     */
    public static hasFlag(enumValue: number, flag: number): boolean {
        return (enumValue & flag) === flag;
    }

    /**
     * Vrací pole dílčích flagů enumu, ze kterého je hodnota složena
     * @param targetEnum Enum
     * @param composedValue Složená hodnota enumu
     */
    public static decomposeFlagEnumValue<T>(targetEnum: T, composedValue: number): number[] {
        const decomposedValues: number[] = [];
        const possibleEnumNumericValues = EnumUtils.getNumericEnumValues(targetEnum);

        for (const numericValue of possibleEnumNumericValues) {
            if (!composedValue)
                break;

            if (composedValue & numericValue) {
                decomposedValues.push(numericValue);
                composedValue -= numericValue;
            }
        }

        return decomposedValues;
    }

    /**
     * Převede číselné hodnoty enumu na textové a spojí je lepidlem
     * @param sourceEnum Typ enumu
     * @param enumValues Hodnoty enumu
     * @param glue Lepidlo spojující jednotlivé položky
     */
    public static formatEnums(sourceEnum: INumericEnum, enumValues: number[], glue: string = ", "): string {
        return enumValues.map(val => sourceEnum[val]).join(glue);
    }
}

GlobalContext.registerType(EnumUtils);
